// Booking flow state machine.
//
// One useReducer drives the entire widget. Each step in the flow
// produces an action; the reducer advances the state and emits a
// step-changed event for analytics. State is intentionally flat —
// React 18 batches updates automatically; we don't need RTK.

import type { Location, FittingType, BookingResponse, PromoDiscount } from '../types';

// MergedSlot is the shape returned by the api-client's getAvailability —
// re-declared here to avoid a circular import.
export interface SelectedSlot {
  start: string;
  end: string;
  fitterId: string;
  fitterName: string;
}

export interface BookingState {
  // Step 1: lead capture (always-visible top of form)
  fullName: string;
  email: string;
  promoOptIn: boolean;

  // Step 2: location search + selection
  locationQuery: string;
  nearbyLocations: Location[];
  selectedLocation: Location | null;

  // Step 3: fitting type
  selectedFittingType: FittingType | null;

  // Step 4: date
  selectedDate: string | null; // YYYY-MM-DD

  // Step 5: fitter preference (user-selected — null = "Any Fitter" intent)
  selectedFitterId: string | null;

  // Step 6: time slot
  // selectedTime is the ISO start; selectedSlot keeps the full slot so we
  // know which fitter to book against when the user chose "Any Fitter"
  selectedTime: string | null;
  selectedSlot: SelectedSlot | null;

  // Step 7: promo + confirm
  promoCode: string;
  /** In-flight POST /waitlist request from the no-availability empty state. */
  waitlistSubmitting: boolean;
  waitlistError: string | null;
  /** In-flight POST /promos/validate request. */
  promoValidating: boolean;
  /** Result of the most recent successful validation call (success or invalid). */
  promoValidation: { valid: boolean; discount?: PromoDiscount; message?: string } | null;
  /** The exact promoCode value that produced `promoValidation`. Used to know
   *  when to invalidate the result (user edited the code after validating). */
  promoValidatedCode: string | null;
  /** Network/transport error from a validate call — distinct from "invalid". */
  promoValidationError: string | null;
  submitting: boolean;
  submitError: string | null;

  // Step 8: confirmation (response from middleware POST /bookings)
  confirmation: BookingResponse | null;

  // Post-booking
  postBookingHandedness: 'left' | 'right' | null;
  postBookingBallFitting: boolean;
  postBookingPhone: string;
  postBookingSmsOptIn: boolean;
  postBookingSubmitting: boolean;
  postBookingError: string | null;
  postBookingComplete: boolean;
}

export type BookingAction =
  | { type: 'SET_LEAD'; payload: Partial<Pick<BookingState, 'fullName' | 'email' | 'promoOptIn'>> }
  | { type: 'SET_LOCATION_QUERY'; payload: string }
  | { type: 'SET_NEARBY_LOCATIONS'; payload: Location[] }
  | { type: 'SELECT_LOCATION'; payload: Location | null }
  | { type: 'SELECT_FITTING_TYPE'; payload: FittingType | null }
  | { type: 'SELECT_DATE'; payload: string | null }
  | { type: 'SELECT_FITTER'; payload: string | null }
  | { type: 'SELECT_TIME'; payload: string | null }
  | { type: 'SELECT_SLOT'; payload: SelectedSlot }
  | {
      type: 'SWITCH_TO_SLOT_AT_LOCATION';
      payload: {
        location: Location;
        fittingType: FittingType;
        date: string;
        slot: SelectedSlot;
      };
    }
  | { type: 'SET_PROMO_CODE'; payload: string }
  | { type: 'VALIDATE_PROMO_START' }
  | {
      type: 'VALIDATE_PROMO_SUCCESS';
      payload: { code: string; valid: boolean; discount?: PromoDiscount; message?: string };
    }
  | { type: 'VALIDATE_PROMO_ERROR'; payload: string }
  | { type: 'WAITLIST_SUBMIT_START' }
  | { type: 'WAITLIST_SUBMIT_SUCCESS'; payload: BookingResponse }
  | { type: 'WAITLIST_SUBMIT_ERROR'; payload: string }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS'; payload: BookingResponse }
  | { type: 'SUBMIT_ERROR'; payload: string }
  | { type: 'CLEAR_SUBMIT_ERROR' }
  | { type: 'POST_BOOKING_FIELD'; payload: Partial<BookingState> }
  | { type: 'POST_BOOKING_SUBMIT_START' }
  | { type: 'POST_BOOKING_SUBMIT_SUCCESS' }
  | { type: 'POST_BOOKING_SUBMIT_ERROR'; payload: string }
  | { type: 'RESET_PRESERVE_LEAD' }
  | { type: 'RESET' };

export const initialState: BookingState = {
  fullName: '',
  email: '',
  promoOptIn: false,
  locationQuery: '',
  nearbyLocations: [],
  selectedLocation: null,
  selectedFittingType: null,
  selectedDate: null,
  selectedFitterId: null,
  selectedTime: null,
  selectedSlot: null,
  promoCode: '',
  waitlistSubmitting: false,
  waitlistError: null,
  promoValidating: false,
  promoValidation: null,
  promoValidatedCode: null,
  promoValidationError: null,
  submitting: false,
  submitError: null,
  confirmation: null,
  postBookingHandedness: null,
  postBookingBallFitting: false,
  postBookingPhone: '',
  postBookingSmsOptIn: false,
  postBookingSubmitting: false,
  postBookingError: null,
  postBookingComplete: false,
};

export function reducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case 'SET_LEAD':
      return { ...state, ...action.payload };

    case 'SET_LOCATION_QUERY':
      return { ...state, locationQuery: action.payload };

    case 'SET_NEARBY_LOCATIONS':
      return { ...state, nearbyLocations: action.payload };

    // The cascade rule for these "SELECT_*" actions is: only invalidate
    // things that genuinely depend on the field being changed. Time + slot
    // depend on every upstream field, so they always clear. Location change
    // clears fitting type, date, fitter, and slots — availability is
    // studio-specific. Date and fitting type are otherwise independent.

    case 'SELECT_LOCATION':
      return {
        ...state,
        selectedLocation: action.payload,
        // Fitting type, date, fitter, and slots are location-scoped — changing
        // studio clears them so the user re-enters in order.
        selectedFittingType: null,
        selectedDate: null,
        selectedFitterId: null,
        selectedTime: null,
        selectedSlot: null,
      };

    case 'SELECT_FITTING_TYPE':
      return {
        ...state,
        selectedFittingType: action.payload,
        // Slots are computed against the fitting type's durationMin, so the
        // current slot is stale. Date and fitter stay.
        selectedTime: null,
        selectedSlot: null,
      };

    case 'SELECT_DATE':
      return {
        ...state,
        selectedDate: action.payload,
        selectedTime: null,
        selectedSlot: null,
      };

    case 'SELECT_FITTER':
      return {
        ...state,
        selectedFitterId: action.payload,
        selectedTime: null,
        selectedSlot: null,
      };

    case 'SELECT_TIME':
      return { ...state, selectedTime: action.payload, selectedSlot: null };

    case 'SELECT_SLOT':
      return {
        ...state,
        selectedTime: action.payload.start,
        selectedSlot: action.payload,
      };

    case 'SWITCH_TO_SLOT_AT_LOCATION':
      // Atomic move when the user picks a slot at a different nearby location
      // from the cross-location expansion. Goes around the per-field cases'
      // cascading resets — sets every field that defines a booking in one shot.
      return {
        ...state,
        selectedLocation: action.payload.location,
        selectedFittingType: action.payload.fittingType,
        selectedDate: action.payload.date,
        selectedFitterId: null,
        selectedTime: action.payload.slot.start,
        selectedSlot: action.payload.slot,
      };

    case 'SET_PROMO_CODE': {
      // Editing the code invalidates any prior validation result — the new
      // string hasn't been validated yet. Keep `promoValidatedCode` so the
      // UI can tell "different from last validated" and re-prompt.
      const codeChanged = action.payload !== state.promoCode;
      return {
        ...state,
        promoCode: action.payload,
        promoValidation: codeChanged ? null : state.promoValidation,
        promoValidationError: codeChanged ? null : state.promoValidationError,
      };
    }

    case 'VALIDATE_PROMO_START':
      return {
        ...state,
        promoValidating: true,
        promoValidationError: null,
      };

    case 'VALIDATE_PROMO_SUCCESS':
      return {
        ...state,
        promoValidating: false,
        promoValidation: {
          valid: action.payload.valid,
          discount: action.payload.discount,
          message: action.payload.message,
        },
        promoValidatedCode: action.payload.code,
        promoValidationError: null,
      };

    case 'VALIDATE_PROMO_ERROR':
      return {
        ...state,
        promoValidating: false,
        promoValidation: null,
        promoValidationError: action.payload,
      };

    case 'SUBMIT_START':
      return { ...state, submitting: true, submitError: null };

    case 'SUBMIT_SUCCESS':
      return { ...state, submitting: false, confirmation: action.payload };

    case 'SUBMIT_ERROR':
      return { ...state, submitting: false, submitError: action.payload };

    case 'CLEAR_SUBMIT_ERROR':
      return { ...state, submitError: null };

    case 'WAITLIST_SUBMIT_START':
      return { ...state, waitlistSubmitting: true, waitlistError: null };

    case 'WAITLIST_SUBMIT_SUCCESS':
      // Lands in the same `confirmation` field as a normal booking. The
      // status discriminator ('waitlisted' vs 'confirmed') drives downstream
      // UI copy (BookingConfirmed renders waitlist messaging when set).
      return {
        ...state,
        waitlistSubmitting: false,
        confirmation: action.payload,
      };

    case 'WAITLIST_SUBMIT_ERROR':
      return { ...state, waitlistSubmitting: false, waitlistError: action.payload };

    case 'POST_BOOKING_FIELD':
      return { ...state, ...action.payload };

    case 'POST_BOOKING_SUBMIT_START':
      return { ...state, postBookingSubmitting: true, postBookingError: null };

    case 'POST_BOOKING_SUBMIT_SUCCESS':
      return {
        ...state,
        postBookingSubmitting: false,
        postBookingError: null,
        postBookingComplete: true,
      };

    case 'POST_BOOKING_SUBMIT_ERROR':
      return { ...state, postBookingSubmitting: false, postBookingError: action.payload };

    case 'RESET':
      return initialState;

    case 'RESET_PRESERVE_LEAD':
      return { ...initialState, fullName: state.fullName, email: state.email, promoOptIn: state.promoOptIn };
  }
}
